import { ALL_MARKET_CODES, ALL_MARKET_NAMES, type AllMarketCode } from '@hc/shared';

// Per-market data provenance — the display-layer register of where each of the
// 27 served markets' company spine comes from. Two pipeline kinds:
//
//   • 'pipeline'     — our own ingest services pull the national register /
//                      statutory filings directly (GB FR EE NO DK BE).
//   • 'commissioned' — the market dataset is built and reconciled by the
//                      research fleet from the named national register(s),
//                      loaded via the staged merge with join-verified registry
//                      ids (the id_basis contract).
//
// The registry names + id keyspaces mirror the authoritative reconciliation
// list (handover/reconciliation/Platform_RegistryId_Keyspace_List_2026-07-15.md,
// cross-checked against live entities rows). If a market's key moves in
// merge-staging.ts, update it here too — this file is what the Sources and
// Coverage panels show users.

export type PipelineKind = 'pipeline' | 'commissioned';

export interface MarketProvenance {
  code: AllMarketCode;
  name: string;
  kind: PipelineKind;
  /** Company & ownership spine — sources, with the registry id keyspace. */
  company: string;
  /** Demographics & demand sources. */
  demographics: string;
}

const COMPANY: Record<AllMarketCode, { kind: PipelineKind; company: string }> = {
  GB: {
    kind: 'pipeline',
    company:
      'Companies House (entities, filings & officers — company number) · uk_company_enrichment (accounts, total assets, owner age) · CQC, Care Inspectorate, CIW, RQIA (sites & ratings).',
  },
  FR: {
    kind: 'pipeline',
    company:
      'SIRENE (establishments — SIREN) · INPI (annual accounts, beneficial ownership & dates of birth) · FINESS (health facilities) · RPPS (practitioners).',
  },
  BE: {
    kind: 'pipeline',
    company:
      'Crossroads Bank for Enterprises (KBO/BCE — dotted 12-digit id) · NBB Central Balance Sheet (filings) · INAMI/RIZIV (practitioners & convention status).',
  },
  NO: {
    kind: 'pipeline',
    company:
      'Brønnøysund Enhetsregisteret / Regnskapsregisteret (orgnr — register & accounts) · HPR practitioner register · Helfo contracts.',
  },
  DK: {
    kind: 'pipeline',
    company:
      'Datafordeler CVR (CVR-nummer — register, roles, distress events) · Erhvervsstyrelsen accounts · STPS authorisation register.',
  },
  EE: {
    kind: 'pipeline',
    company:
      'RIK Äriregister (registrikood — register, officers, annual reports) · Terviseamet activity licences · Tervisekassa contracts.',
  },
  AT: { kind: 'commissioned', company: 'Firmenbuch (Firmenbuchnummer, e.g. FN492011X).' },
  BG: { kind: 'commissioned', company: 'Registry Agency Commercial Register (EIK).' },
  CH: { kind: 'commissioned', company: 'UID register / Zefix (CHE-number).' },
  CZ: { kind: 'commissioned', company: 'ARES (IČO).' },
  DE: {
    kind: 'commissioned',
    company:
      'Handelsregister (court + HRB composite id) · Bundesanzeiger filings · Träger XML → Handelsregister crosswalk for quality joins.',
  },
  ES: {
    kind: 'commissioned',
    company: 'REGCESS health-centre register (CCN) · INE official counts back the coverage layer.',
  },
  FI: { kind: 'commissioned', company: 'PRH / YTJ business register (y-tunnus).' },
  GR: { kind: 'commissioned', company: 'Tax registry keyspace (ΑΦΜ) · national health registers.' },
  HR: {
    kind: 'commissioned',
    company:
      'Health-provider registers; no clean national company key — entities carry name-based synthetic ids.',
  },
  HU: { kind: 'commissioned', company: 'Cégjegyzék court company register (court-prefixed id).' },
  IE: { kind: 'commissioned', company: 'Companies Registration Office (CRO number).' },
  IT: { kind: 'commissioned', company: 'Registro Imprese (P.IVA / codice fiscale).' },
  LT: { kind: 'commissioned', company: 'Registrų centras (juridinio asmens kodas).' },
  LV: { kind: 'commissioned', company: 'Uzņēmumu reģistrs (reģistrācijas numurs).' },
  NL: { kind: 'commissioned', company: 'KVK Handelsregister (KvK-nummer).' },
  PL: {
    kind: 'commissioned',
    company: 'NIP-keyed national registers (KRS / CEIDG ecosystems; NIP, not REGON, is the key).',
  },
  PT: {
    kind: 'commissioned',
    company: 'Health-provider registers keyed to NIPC (ERS registration universe).',
  },
  RO: { kind: 'commissioned', company: 'ONRC trade register (CUI) · ANMCS quality register.' },
  SE: { kind: 'commissioned', company: 'Bolagsverket (organisationsnummer) · IVO care register.' },
  SI: {
    kind: 'commissioned',
    company: 'NIJZ RIZDDZ health-provider register (RIZDDZ-prefixed ids — not matična številka).',
  },
  SK: { kind: 'commissioned', company: 'Register organizácií / ORSR (IČO).' },
};

const DEMOG_NATIVE: Partial<Record<AllMarketCode, string>> = {
  GB: 'ONS / NOMIS & NISRA — census, model-based household income, 2022-based population projections.',
  FR: 'INSEE — census & income.',
  BE: 'Statbel · Eurostat EUROPOP2025 regional projections.',
  NO: 'SSB (Statistics Norway) · Eurostat EUROPOP2025 regional projections.',
  DK: 'Danmarks Statistik · Eurostat EUROPOP2025 regional projections.',
  EE: 'Statistikaamet · Eurostat EUROPOP2025 regional projections.',
};

const DEMOG_DEFAULT =
  'Eurostat — census aggregates & EUROPOP2025 regional population projections (NUTS-2).';

export const MARKET_PROVENANCE: ReadonlyArray<MarketProvenance> = [...ALL_MARKET_CODES]
  .map((code) => ({
    code,
    name: ALL_MARKET_NAMES[code],
    kind: COMPANY[code].kind,
    company: COMPANY[code].company,
    demographics: DEMOG_NATIVE[code] ?? DEMOG_DEFAULT,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const PROVENANCE_BY_CODE: ReadonlyMap<string, MarketProvenance> = new Map(
  MARKET_PROVENANCE.map((m) => [m.code as string, m]),
);
